import { FC, Fragment, useState } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled from 'styled-components';

import ClickableButton from '@/components/Buttons/ClickableButton';
import ScrollToTop from '@/components/Buttons/ScrollToTop';
import Container from '@/components/Container/Container';
import Footer from '@/components/Footer/Footer';
import FooterBanner from '@/components/FooterBanner/FooterBanner';
import HubspotForm from '@/components/Modal/HubspotForm';
import HubspotFormModal from '@/components/Modal/HubspotFormModal';
import Navigation from '@/components/Navigation/Navigation';
import Seo from '@/components/Seo/Seo';
import {
	HUBSPOT_FORM_IDS,
	HUBSPOT_FORM_TITLES,
	LANGUAGES,
} from '@/lib/constants';
import { fetchAPI, getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import Item from '@/modules/viber-for-business/Item';

const ViberForBusiness: FC<any> = ({ data, lang }) => {
	const [currentFormId, setCurrentFormId] = useState<string | null>(null);

	const openModal = (formId) => setCurrentFormId(formId);

	const closeModal = () => setCurrentFormId(null);

	const getTitle = (num: number) =>
		translate(lang, {
			en: `Viber for Business: Marketing on Viber ${num}`,
			ru: `Viber для бизнеса: маркетинг в Viber ${num}`,
			ua: `Viber для бізнесу: маркетинг в Viber ${num}`,
		});

	return (
		<>
			<Seo
				url="/viber-for-business"
				title={translate(lang, {
					en: 'Viber for Business: Marketing on Viber',
					ru: 'Viber для бизнеса: маркетинг в Viber',
					ua: 'Viber для бізнесу: маркетинг в Viber',
				})}
				description={translate(lang, {
					en: 'Find out what Viber can do for your business. Advertising solutions and Messaging solution. Two complementary solutions, one result: better customer relationships.',
					ru: 'Узнайте, как Viber может помочь вашему бизнесу. Выстраивайте отношения c вашими клиентами и исследуйте новые возможности с Viber.',
					ua: 'Дізнайтеся, як Viber може допомогти вашому бізнесу. Налагоджуйте відносини з вашими клієнтами і досліджуйте нові можливості з Viber.',
				})}
			/>

			<Navigation lang={lang} />

			<Wrapper>
				<Banner>
					<Lefthand alt={getTitle(1)} src="/images/viconnect/leftHand.png" />

					<Righthand alt={getTitle(2)} src="/images/viconnect/rightHand.png" />

					<Line
						loading="lazy"
						alt="Gradient"
						src="/images/viconnect/heroLine.png"
					/>

					<Grad
						loading="lazy"
						alt="Gradient"
						src="/images/viconnect/back.png"
					/>

					<ScrollAnimation animateOnce animateIn="fadeInUp">
						<Heading>
							<Center>
								{data?.hero?.title ? <Title>{data?.hero.title}</Title> : null}

								{data?.hero?.description ? (
									<Paragraph>{data?.hero.description}</Paragraph>
								) : null}
							</Center>
						</Heading>
					</ScrollAnimation>
				</Banner>

				<Solutions>
					<Solution href={`/${lang}/marketing-solutions`} rel="nofollow">
						<ScrollAnimation animateOnce animateIn="fadeInUp" offset={0}>
							<SolutionTitle>
								{parseHTML(data?.solutions[0]?.title)}
							</SolutionTitle>

							<SolutionDescription>
								{parseHTML(data?.solutions[0]?.description)}
								<br />
								{parseHTML(data?.solutions[0]?.description2 ?? '')}
							</SolutionDescription>
						</ScrollAnimation>
					</Solution>

					<Solution href={`/${lang}/messaging-solutions`}>
						<ScrollAnimation
							animateOnce
							offset={0}
							delay={200}
							animateIn="fadeInUp"
						>
							<SolutionTitle>
								{parseHTML(data?.solutions[1]?.title)}
							</SolutionTitle>

							<SolutionDescription>
								{parseHTML(data?.solutions[1]?.description)}{' '}
								<SolutionSpan>
									{parseHTML(data?.solutions[1]?.numberBox)}
								</SolutionSpan>{' '}
								<br />
								{parseHTML(data?.solutions[1]?.description2 ?? '')}
							</SolutionDescription>
						</ScrollAnimation>
					</Solution>
				</Solutions>

				<Container>
					<Journeys>
						<CenterImg
							loading="lazy"
							alt="Gradient"
							src="/images/viconnect/center.webp"
						/>

						<ScrollAnimation animateOnce animateIn="fadeInUp">
							<JourTitle>{parseHTML(data?.userJourney?.text?.title)}</JourTitle>

							<JourDescription>
								{parseHTML(data?.userJourney?.text?.description)}
							</JourDescription>
						</ScrollAnimation>

						<JourneyContent>
							{data?.userJourney?.touchpoints?.map((item, index) =>
								item.id === 4 || item.id === 8 || item.id === 12 ? (
									<Journey key={`viber_for_business_journey_1_${index}`}>
										<ScrollAnimation
											animateOnce
											offset={0}
											delay={(index + 1) * 200}
											animateIn="fadeInUp"
										>
											<Img alt={item?.title} src={getImage(item?.icon)} />

											<Subtitle>{parseHTML(item?.title)}</Subtitle>
										</ScrollAnimation>
									</Journey>
								) : (
									<Fragment key={`viber_for_business_journey_2_${index}`}>
										<Journey>
											<ScrollAnimation
												animateOnce
												offset={0}
												animateIn="fadeInUp"
												delay={(index + 1) * 200}
											>
												<Img alt={item?.title} src={getImage(item?.icon)} />

												<Subtitle>{parseHTML(item?.title)}</Subtitle>
											</ScrollAnimation>
										</Journey>

										<ScrollAnimation
											animateOnce
											offset={0}
											delay={(index + 1) * 200}
											animateIn="fadeInUp"
										>
											{index !== data?.userJourney?.touchpoints?.length - 1 && (
												<Arrow
													alt="Arrow next"
													src="/images/viconnect/arr.svg"
												/>
											)}
										</ScrollAnimation>
									</Fragment>
								),
							)}
						</JourneyContent>
					</Journeys>

					<ViContainer>
						<Left
							loading="lazy"
							alt="Gradient"
							src="/images/viconnect/left1.webp"
						/>

						<PhonesAttract>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<picture>
									<PhoneImg
										alt={data?.attract?.phone?.name}
										src={getImage(data?.attract?.phone)}
									/>
								</picture>
							</ScrollAnimation>
						</PhonesAttract>

						<Vi>
							<ScrollAnimation animateOnce animateIn="fadeInUp" offset={0}>
								<ViTitleContainer>
									<ViIcon
										alt={data?.attract?.title}
										src={getImage(data?.attract?.logo)}
									/>

									<ViTitle>{parseHTML(data?.attract?.title)}</ViTitle>
								</ViTitleContainer>
							</ScrollAnimation>

							<Items>
								{data?.attract?.bulletPoints?.map((item, index) => (
									<ScrollAnimation
										key={`viber_for_business_bullet_point_1_${index}`}
										animateOnce
										offset={0}
										delay={index * 100}
										animateIn="fadeInUp"
									>
										<Item
											src="https://env-stage-hosting.s3.eu-central-1.amazonaws.com/heart_Icon_d53e9c4a17.svg"
											alt="heartIcon"
											desc={item?.description}
										/>
									</ScrollAnimation>
								))}
							</Items>
						</Vi>
					</ViContainer>

					<ViContainer>
						<Vi>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<ViTitleContainer>
									<ViIcon
										alt={data?.interact?.title}
										src={getImage(data?.interact?.logo)}
									/>

									<ViTitle>{parseHTML(data?.interact?.title)}</ViTitle>
								</ViTitleContainer>
							</ScrollAnimation>

							<Items>
								{data?.interact?.bulletPoints?.map((item, index) => (
									<ScrollAnimation
										key={`viber_for_business_bullet_point_2_${index}`}
										animateOnce
										offset={0}
										delay={index * 100}
										animateIn="fadeInUp"
									>
										<Item
											alt="heartIcon"
											desc={item?.description}
											src="https://env-stage-hosting.s3.eu-central-1.amazonaws.com/heart_Icon_d53e9c4a17.svg"
										/>
									</ScrollAnimation>
								))}
							</Items>
						</Vi>

						<PhonesInteract>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<picture>
									<PhoneImg
										src={getImage(data?.interact?.phone)}
										alt={data?.interact?.phone?.name}
									/>
								</picture>
							</ScrollAnimation>
						</PhonesInteract>

						<Right
							loading="lazy"
							alt="Gradient"
							src="/images/viconnect/right1.png"
						/>
					</ViContainer>

					<ViContainer>
						<LeftSecond
							loading="lazy"
							alt="Gradient"
							src="/images/viconnect/left2.png"
						/>

						<PhonesTransact>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<picture>
									<PhoneImg
										loading="lazy"
										alt={data?.transact?.phone?.name}
										src={getImage(data?.transact?.phone)}
									/>
								</picture>
							</ScrollAnimation>
						</PhonesTransact>

						<Vi>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<ViTitleContainer>
									<ViIcon
										alt={data?.transact?.logo?.name}
										src={getImage(data?.transact?.logo)}
									/>

									<ViTitle>{parseHTML(data?.transact?.title)}</ViTitle>
								</ViTitleContainer>
							</ScrollAnimation>

							<Items>
								{data?.transact?.bulletPoints?.map((item, index) => (
									<ScrollAnimation
										key={`viber_for_business_bullet_point_3_${index}`}
										animateOnce
										offset={0}
										delay={index * 100}
										animateIn="fadeInUp"
									>
										<Item
											src="https://env-stage-hosting.s3.eu-central-1.amazonaws.com/heart_Icon_d53e9c4a17.svg"
											alt="heartIcon"
											desc={item?.description}
										/>
									</ScrollAnimation>
								))}
							</Items>
						</Vi>
					</ViContainer>

					<ViContainer>
						<Vi>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<ViTitleContainer>
									<ViIcon
										alt={data?.stay?.logo?.name}
										src={getImage(data?.stay?.logo)}
									/>

									<ViTitle>{parseHTML(data?.stay?.title)}</ViTitle>
								</ViTitleContainer>
							</ScrollAnimation>

							<Items>
								{data?.stay?.bulletPoints?.map((item, index) => (
									<ScrollAnimation
										key={`viber_for_business_bullet_point_4_${index}`}
										animateOnce
										offset={0}
										delay={index * 100}
										animateIn="fadeInUp"
									>
										<Item
											alt="heartIcon"
											desc={item?.description}
											src="https://env-stage-hosting.s3.eu-central-1.amazonaws.com/heart_Icon_d53e9c4a17.svg"
										/>
									</ScrollAnimation>
								))}
							</Items>
						</Vi>

						<PhonesStayContact>
							<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
								<picture>
									<PhoneImg
										loading="lazy"
										alt={data?.stay?.phone?.name}
										src={getImage(data?.stay?.phone)}
									/>
								</picture>
							</ScrollAnimation>
						</PhonesStayContact>

						<RightSecond
							loading="lazy"
							alt="Gradient"
							src="/images/viconnect/right2.png"
						/>
					</ViContainer>

					<ScrollAnimation animateOnce offset={0} animateIn="fadeInUp">
						<GetStartedContainer>
							<GetLeft>
								<GetTitle>{parseHTML(data?.ctaBlock?.title)}</GetTitle>

								<GetDescription>
									{parseHTML(data?.ctaBlock?.description)}
								</GetDescription>
							</GetLeft>

							<GetRight>
								<ScrollAnimation
									animateOnce
									offset={0}
									delay={200}
									animateIn="fadeInUp"
								>
									<ClickableButton
										text={data?.ctaBlock?.cta?.text}
										onClick={() => openModal(HUBSPOT_FORM_IDS.MAIN)}
									/>
								</ScrollAnimation>
							</GetRight>
						</GetStartedContainer>
					</ScrollAnimation>

					<ScrollToTop />
				</Container>
			</Wrapper>

			<FooterBanner
				responsive
				viberForBusiness
				title={translate(lang, {
					en: 'See how Viber for Business helps brands grow',
					ru: 'Узнайте о том, как Viber для бизнеса помог брендам достичь успеха',
					ua: 'Дізнайтесь, як рішення Viber допомагають брендам зростати',
				})}
				cta={{
					text: translate(lang, {
						en: 'Success stories',
						ru: 'Истории успеха',
						ua: 'Історії успіху',
					}),
					link: '/success-stories',
				}}
				image={{
					link: '/images/messaging-solutions/bottom-phone.webp',
					alt: translate(lang, {
						en: 'Viber for Business: Marketing on Viber 15',
						ru: 'Viber для бизнеса: маркетинг в Viber 15',
						ua: 'Viber для бізнесу: маркетинг в Viber 15',
					}),
				}}
			/>

			<Footer lang={lang} />

			{currentFormId ? (
				<HubspotFormModal handleClose={closeModal}>
					<h2>{HUBSPOT_FORM_TITLES.MAIN}</h2>

					<HubspotForm formId={currentFormId} handleClose={closeModal} />
				</HubspotFormModal>
			) : null}
		</>
	);
};

const CenterImg = styled.img`
	position: absolute;
	top: -50%;
	right: 0;
	width: 100%;

	@media (max-width: 800px) {
		top: 30%;
	}
`;

const Left = styled.img`
	position: absolute;
	bottom: -57%;
	left: -23%;
	transform: rotate(-184.42deg);

	@media (max-width: 1000px) {
		width: 100%;
	}

	@media (max-width: 800px) {
		bottom: -100%;
		width: 100%;
	}

	@media (max-width: 600px) {
		max-width: 350px;
		bottom: -11%;
	}
`;

const LeftSecond = styled.img`
	position: absolute;
	bottom: -57%;
	left: -23%;
	transform: rotate(0deg);

	@media (max-width: 800px) {
		bottom: -40%;
		width: 100%;
	}

	@media (max-width: 600px) {
		max-width: 350px;
		bottom: -15%;
	}
`;

const RightSecond = styled.img`
	position: absolute;
	right: 5%;
	bottom: top;
	top: -70%;

	@media (max-width: 800px) {
		top: 0;
	}

	@media (max-width: 600px) {
		right: 0;
	}
`;

const Right = styled.img`
	position: absolute;
	right: 0;
	bottom: 0;
	top: -80%;

	@media (max-width: 800px) {
		top: -130%;
	}

	@media (max-width: 600px) {
		top: -10%;
	}
`;

const Wrapper = styled.div`
	padding-bottom: 50px;
`;

const Lefthand = styled.img`
	position: absolute;
	left: 0;
	top: 30%;
	z-index: 3;
	width: 20%;

	@media (max-width: 900px) {
		top: 55%;
	}

	@media (max-width: 600px) {
		top: 60%;
		width: 27%;
	}

	@media (max-width: 400px) {
		top: 70%;
	}
`;

const Righthand = styled.img`
	position: absolute;
	right: 0;
	top: 30%;
	z-index: 3;
	width: 20%;

	@media (max-width: 900px) {
		top: 55%;
	}

	@media (max-width: 600px) {
		top: 60%;
		width: 30%;
	}

	@media (max-width: 400px) {
		top: 70%;
	}
`;

const Line = styled.img`
	position: absolute;
	top: 45%;
	z-index: 2;
	right: 0;
	width: 100%;

	@media (min-width: 1440px) {
		top: 40%;
	}

	@media (min-width: 1670px) {
		top: 35%;
	}

	@media (min-width: 1850px) {
		top: 25%;
	}

	@media (max-width: 900px) {
		top: 55%;
	}

	@media (max-width: 600px) {
		top: 70%;
	}
`;

const Grad = styled.img`
	left: 0;
	position: absolute;

	@media (max-width: 800px) {
		width: 100%;
	}
`;

const Banner = styled.div`
	min-height: 550px;
	position: relative;
	margin-top: 80px;
	background-repeat: no-repeat;
	background-position: center;
	background-size: cover;
	background-position-y: -60px;

	@media (min-width: 1700px) {
		min-height: 600px;
		background-position-y: -150px;
	}

	@media (min-width: 2000px) {
		min-height: 700px;
		background-position-y: -200px;
	}

	@media (min-width: 2300px) {
		min-height: 700px;
		background-position-y: -250px;
	}

	@media (max-width: 1200px) {
		min-height: 480px;
	}

	@media (max-width: 800px) {
		background-position-y: 100px;
	}

	@media (max-width: 600px) {
		min-height: 500px;
		background-position-y: -20px;
	}

	@media (max-width: 550px) {
		background-position-y: -200px;
	}

	@media (max-width: 400px) {
		background-position-y: -20px;
	}
`;

const Heading = styled.div`
	padding-top: 80px;
	z-index: 5;

	@media (max-width: 900px) {
		padding-top: 40px;
	}

	@media (max-width: 600px) {
		padding-top: 50px;
	}
`;

const Center = styled.div`
	text-align: center;
`;

const Title = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 65px;
	line-height: 60px;
	color: var(--color-purple);

	@media (max-width: 800px) {
		font-size: 40px;
	}
`;

const Paragraph = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	text-align: center;
	width: 42%;
	margin: 0 auto;
	margin-top: 40px;

	@media (max-width: 1200px) {
		width: 60%;
	}

	@media (max-width: 900px) {
		width: 90%;
	}

	@media (max-width: 800px) {
		font-size: 22px;
		width: 90%;
		margin-top: 20px;
	}
`;

const Solutions = styled.div`
	display: flex;
	flex-wrap: wrap;

	@media (max-width: 600px) {
		flex-direction: column;
	}
`;

const Solution = styled.a`
	width: 50%;
	text-align: center;
	align-items: center;
	text-decoration: none;
	display: flex;
	flex-direction: column;
	padding: 40px 120px 30px;
	border: 2px solid var(--color-white-30);
	transition: 0.2s;

	&:hover {
		border: 2px solid var(--color-purple);
	}

	@media (max-width: 1340px) {
		padding: 53px 50px 30px;
	}

	@media (max-width: 1080px) {
		padding: 53px 90px 30px;
	}

	@media (max-width: 810px) {
		padding: 53px 50px 30px;
	}

	@media (max-width: 680px) {
		padding: 53px 10px 30px;
	}

	@media (max-width: 600px) {
		padding: 53px 45px 30px;
		width: 100%;
	}
`;

const GetStartedContainer = styled.div`
	display: flex;
	width: 1180px;
	max-width: 100%;
	margin: 0 auto;
	background-color: var(--color-white-30);
	mix-blend-mode: normal;
	border-radius: 60px;
	padding: 50px 60px;
	justify-content: space-between;
	align-items: flex-end;
	margin-top: 120px;
	margin-bottom: 150px;

	@media (max-width: 1900px) {
		width: 990px;
		max-width: 100%;
	}

	@media (max-width: 1440px) {
		width: 960px;
		max-width: 100%;
	}

	@media (max-width: 600px) {
		width: 100%;
		padding: 40px 24px 40px 30px;
		flex-direction: column;
		align-items: flex-start;
		margin-top: 0;
		margin-bottom: 50px;
		border-radius: 30px;
	}
`;

const GetLeft = styled.div`
	@media (max-width: 1440px) {
		width: 400px;
		max-width: 100%;
	}

	@media (max-width: 800px) {
		width: 330px;
		max-width: 100%;
	}

	@media (max-width: 700px) {
		width: 295px;
		max-width: 100%;
	}

	@media (max-width: 600px) {
		width: 100%;
		margin-bottom: 30px;
	}
`;

const GetRight = styled.div`
	display: flex;
	justify-content: flex-end;

	@media (max-width: 600px) {
		width: 100%;
	}

	div:first-of-type {
		width: 100%;
	}
`;

const GetTitle = styled.h3`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	color: var(--color-black);
	margin-bottom: 20px;

	@media (max-width: 600px) {
		font-size: 22px;
		margin-bottom: 20px;
	}
`;

const GetDescription = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	color: var(--color-black);
`;

const SolutionTitle = styled.h3`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple);
	margin-bottom: 15px;

	@media (max-width: 600px) {
		font-size: 22px;
	}
`;

const SolutionDescription = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple-50);
	min-height: 80px;
`;

const SolutionSpan = styled.span`
	margin: 0;
	border: 1px solid var(--color-purple-50);
	padding: 3px 6px;
	border-radius: 24px;
`;

const Journeys = styled.div`
	width: 1180px;
	max-width: 100%;
	margin: 0 auto;
	margin-top: 120px;
	position: relative;

	@media (max-width: 1900px) {
		width: 990px;
		max-width: 100%;
	}

	@media (max-width: 1440px) {
		width: 960px;
		max-width: 100%;
	}

	@media (max-width: 600px) {
		margin-top: 55px;
	}
`;

const JourTitle = styled.h2`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	text-align: center;
	color: var(--color-purple);
	margin-bottom: 20px;

	@media (max-width: 600px) {
		font-size: 28px;
		line-height: 32px;
	}
`;

const JourDescription = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	color: var(--color-black);
	text-align: center;
`;

const Journey = styled.div`
	border-radius: 30px;
	text-align: center;
	width: 185px;

	@media (max-width: 800px) {
		padding: 20px 20px 30px;
	}

	@media (max-width: 600px) {
		padding: 20px 15px 20px;
		width: 22%;
		margin-bottom: 10px;
		border-radius: 20px;
	}

	@media (max-width: 440px) {
		padding: 0;
	}
`;

const Img = styled.img`
	@media (max-width: 600px) {
		width: 44px;
	}
`;

const Subtitle = styled.h3`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	color: var(--color-black);
	min-height: 70px;
	margin-top: 20px;

	@media (max-width: 1600px) {
		font-size: 21px;
	}

	@media (max-width: 800px) {
		font-size: 17px;
	}

	@media (max-width: 600px) {
		font-family: var(--font-sans);

		font-size: 14px;
		line-height: 18px;
		text-align: center;
	}
`;

const JourneyContent = styled.div`
	display: flex;
	z-index: 10;
	justify-content: space-around;
	align-items: center;
	margin-top: 60px;
`;

const Arrow = styled.img`
	padding-bottom: 85px;

	@media (max-width: 600px) {
		margin-bottom: 10px;
		width: 15px;
	}
`;

const PhoneImg = styled.img`
	width: 100%;
`;

const PhonesAttract = styled.div`
	max-width: 430px;
	width: 100%;
	height: 555px;
	z-index: 10;

	@media (max-width: 980px) {
		max-width: 304px;
		height: 369px;
		text-align: center;
		margin: 0 auto;
	}

	@media (max-width: 600px) {
		display: flex;
		justify-content: center;
	}
`;

const PhonesInteract = styled.div`
	max-width: 430px;
	width: 100%;
	height: 400px;
	z-index: 10;

	@media (max-width: 980px) {
		max-width: 304px;
		height: 270px;
		margin: 0 auto;
		text-align: center;
	}

	@media (max-width: 600px) {
		display: flex;
		justify-content: center;
	}
`;

const PhonesTransact = styled.div`
	max-width: 430px;
	width: 100%;
	height: 450px;
	z-index: 10;

	@media (max-width: 980px) {
		max-width: 304px;
		height: 270px;
		margin: 0 auto;
		text-align: center;
	}

	@media (max-width: 600px) {
		display: flex;
		justify-content: center;
	}
`;

const PhonesStayContact = styled.div`
	max-width: 430px;
	width: 100%;
	height: 400px;
	z-index: 10;

	@media (max-width: 980px) {
		max-width: 304px;
		height: 270px;
		margin: 0 auto;
		text-align: center;
	}

	@media (max-width: 600px) {
		display: flex;
		justify-content: center;
	}
`;

const ViIcon = styled.img`
	margin-bottom: 10px;
`;

const ViTitle = styled.h2`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	color: var(--color-purple);
	margin-left: 15px;

	@media (max-width: 600px) {
		font-size: 28px;
		line-height: 32px;
	}
`;

const ViTitleContainer = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 50px;

	@media (max-width: 980px) {
		margin-bottom: 25px;
	}
`;

const Items = styled.span`
	padding-left: 10px;
`;

const Vi = styled.div`
	max-width: 530px;
	padding-right: 70px;
	display: flex;
	flex-direction: column;
	justify-content: center;

	@media (max-width: 980px) {
		padding-right: 0px;
		width: 100%;
	}

	&:nth-of-type(2) {
		@media (max-width: 1200px) {
			padding-left: 100px;
			padding-right: 0px;
		}
		@media (max-width: 980px) {
			padding-left: 0px;
			padding-right: 0px;
		}
	}

	&:nth-of-type(4) {
		@media (max-width: 1200px) {
			padding-left: 100px;
			padding-right: 0px;
		}
		@media (max-width: 980px) {
			padding-left: 0px;
			padding-right: 0px;
		}
	}

	@media (max-width: 600px) {
		padding: 0 !important;
	}
`;

const ViContainer = styled.div`
	display: flex;
	position: relative;
	justify-content: space-between;
	width: 1180px;
	max-width: 100%;
	margin: 0 auto;
	margin-top: 120px;

	.animated {
		position: relative;
		z-index: 1;
	}

	&:nth-child(even) {
		${Vi} {
			padding-right: 0;
			padding-left: 70px;
			@media (max-width: 979px) {
				padding: 0;
			}
		}
	}

	@media (max-width: 1900px) {
		width: 990px;
		max-width: 100%;
	}

	@media (max-width: 1440px) {
		width: 960px;
		max-width: 100%;
	}

	@media (max-width: 980px) {
		flex-direction: column;
		width: 90%;
		margin-top: 55px;
	}

	&:nth-of-type(2) {
		@media (max-width: 980px) {
			flex-direction: column-reverse;
		}

		@media (max-width: 600px) {
			margin-top: 0;
			width: 100%;
		}
	}

	&:nth-of-type(4) {
		@media (max-width: 980px) {
			flex-direction: column-reverse;
		}
	}

	@media (max-width: 600px) {
		margin-top: 0;
		margin-bottom: 50px;
		width: 100%;
	}
`;

export async function getStaticProps({ params }) {
	const { lang } = params;
	const page = await fetchAPI('/viber-for-business', {
		lang,
		populate: [
			'hero',
			'solutions',
			'userJourney',
			'userJourney.touchpoints',
			'userJourney.touchpoints.icon',
			'attract',
			'attract.logo',
			'attract.logo.file',
			'attract.phone',
			'attract.bulletPoints',
			'interact',
			'interact.logo',
			'interact.logo.file',
			'interact.phone',
			'interact.bulletPoints',
			'transact',
			'transact.logo',
			'transact.logo.file',
			'transact.phone',
			'transact.bulletPoints',
			'stay',
			'stay.logo',
			'stay.logo.file',
			'stay.phone',
			'stay.bulletPoints',
			'ctaBlock',
			'ctaBlock.cta',
			'bottomBanner',
			'bottomBanner.cta',
			'bottomBanner.image',
		],
	});

	return {
		props: { data: page?.data, lang },
	};
}

export async function getStaticPaths() {
	const paths = LANGUAGES.map(({ code }) => ({ params: { lang: code } }));

	return { paths, fallback: false };
}

export default ViberForBusiness;
